import { Menu, X } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { version } from '@/../package.json';
import MapFilters from '@/components/MapFilters/MapFilters';
import { showErrorToast } from '@/components/ToastNotification';
import GlobalContext from '@/context/GlobalContext';
import { useAuth } from '@/hooks/useAuth';
import { useWindowSize } from '@/hooks/useWindowSize';
import { supabase } from '@/lib/supabase';
import type { DisciplineWithCourses } from '@/lib/types';

import './PublicNavbar.css';

const AdminNavbar = () => {
	const { disciplines } = useContext(GlobalContext);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const { user } = useAuth();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const [width] = useWindowSize();

	// biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger, not used inside the effect body
	useEffect(() => {
		setIsMobileMenuOpen(false);
	}, [pathname]);

	const signOut = async () => {
		setIsMobileMenuOpen(false);
		const { error } = await supabase.auth.signOut();
		if (error) {
			showErrorToast('Unable to sign out...please try again.');
			return;
		}
		navigate('/');
	};

	const isMobile = width < 900;

	return (
		<div className="relative mt-4" style={{ gridArea: 'nav', zIndex: 20 }}>
			{/* ── Navbar ── */}
			<nav
				className="flex items-center gap-2 bg-white px-4 py-2 condensed:px-6 condensed:py-3"
			>
				{/* ── Map search — desktop only ── */}
				{!isMobile && pathname === '/dashboard/course-map' && (
					<MapFilters dropdownOptions={(disciplines ?? []) as DisciplineWithCourses[]} />
				)}

				{/* Spacer — pushes menu button right */}
				<div className="flex-1" />

				{/* ── Pill menu button — mobile only ── */}
				<button
					type="button"
					aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
					aria-expanded={isMobileMenuOpen}
					onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
					className="small-screen:hidden flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold text-white cursor-pointer border-0 transition-opacity hover:opacity-90 focus:outline-none"
					style={{ backgroundColor: 'var(--org-color)', fontFamily: 'var(--font-semibold)' }}
				>
					{isMobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
					<span>{isMobileMenuOpen ? 'Close' : 'Menu'}</span>
				</button>
			</nav>

			{/* ── Mobile menu — outside nav, inside wrapper ── */}
			{isMobileMenuOpen && (
				<>
					{/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismiss overlay */}
					<div
						role="presentation"
						className="fixed inset-0 bg-black/40 small-screen:hidden"
						style={{ zIndex: 1 }}
						onClick={() => setIsMobileMenuOpen(false)}
						onKeyDown={() => setIsMobileMenuOpen(false)}
					/>

					{/* Panel — above backdrop, positioned just below the navbar */}
					<div
						className="navbar-panel-animate absolute top-[calc(100%+8px)] left-3 right-3 small-screen:hidden overflow-hidden rounded-2xl max-w-[500px] ml-auto"
						style={{
							zIndex: 2,
							backgroundColor: 'var(--elevate-primary)',
							boxShadow: '0 16px 40px rgba(0,0,0,0.3)',
						}}
					>
						<div className="flex flex-col px-2 py-2">
							{/* ── User + Sign Out ── */}
							<div className="flex flex-col gap-1 px-2 pt-1">
								{user && (
									<div className="flex items-center gap-3 py-2">
										<div
											className="rounded-full shrink-0 h-10 w-10 overflow-hidden flex items-center justify-center bg-[#737295] text-white text-sm select-none"
											style={{ fontFamily: 'var(--font-semibold)' }}
										>
											{user.email?.split('')[0].toUpperCase()}
										</div>
										<span className="text-sm text-white/60 truncate">{user.email}</span>
									</div>
								)}

								<div className="flex items-center gap-2 pt-1 pb-2">
									{user && (
										<button
											type="button"
											onClick={signOut}
											className="flex-1 flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-white/80 hover:text-white bg-white/10 hover:bg-white/15 border-0 cursor-pointer transition-colors"
											style={{ fontFamily: 'var(--font-semibold)' }}
										>
											Sign Out
										</button>
									)}
								</div>

								{/* Privacy + copyright */}
								<div className="flex items-center justify-between pt-2 pb-1 px-2">
									<span className="text-[11px] text-white/30">
										© {new Date().getFullYear()} Stakk IO · v{version}
									</span>
									<Link
										to="/privacy-policy"
										className="text-[11px] text-white/50 hover:text-white/80 no-underline transition-colors"
										onClick={() => setIsMobileMenuOpen(false)}
									>
										Privacy Policy
									</Link>
								</div>
							</div>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default AdminNavbar;
